"use client";

import { useCallback, useMemo } from "react";
import { toast } from "sonner";

import {
    presetParentCategoryPillLabel,
    useCreatePreset,
    useFavoritePresets,
    usePresetParentCategories,
    usePresets,
    useToggleFavorite,
} from "@/hooks/use-presets";
import { parentCategoryId } from "@/hooks/use-command-v2-empty-presets";
import { mapPresetToV2Display } from "@/components/command-team-v2/utils/map-api-data";
import type { CommandV2PresetDisplay } from "@/components/command-team-v2/types";
import type { ProtoPresetCategory } from "@/components/command-team-v2/prototype/types";

const PRESET_DESCRIPTION_MAX_LENGTH = 500;

export function useCommandV2Presets() {
    const {
        promptItems,
        isLoading: isLoadingPresets,
        hasNextPage,
        isFetchingNextPage,
        fetchNextPage,
    } = usePresets({});
    const { favoriteIds, favoritePromptItems, isLoading: isLoadingFavorites } =
        useFavoritePresets({});
    const { toggleFavorite } = useToggleFavorite();
    const { data: parentCategories } = usePresetParentCategories();
    const createPresetMutation = useCreatePreset();

    const presets: CommandV2PresetDisplay[] = useMemo(() => {
        const merged = new Map<string, CommandV2PresetDisplay>();
        promptItems.forEach((item, index) => {
            merged.set(
                item.id,
                mapPresetToV2Display(item, {
                    favourite: favoriteIds.has(item.id),
                    order: index,
                })
            );
        });
        favoritePromptItems.forEach((item, index) => {
            merged.set(
                item.id,
                mapPresetToV2Display(item, {
                    favourite: true,
                    order: index,
                })
            );
        });
        return [...merged.values()].sort((a, b) => a.order - b.order);
    }, [promptItems, favoritePromptItems, favoriteIds]);

    const createPreset = useCallback(
        (input: { title: string; promptText: string; category?: ProtoPresetCategory }) => {
            const category = input.category ?? "custom";
            const categoryId = parentCategoryId(parentCategories?.items ?? [], category);
            if (!categoryId) {
                toast.error("Failed to save preset", {
                    description: "We couldn't save your preset right now. Please try again.",
                });
                return;
            }
            createPresetMutation.mutate(
                {
                    title: input.title,
                    description: input.promptText.slice(0, PRESET_DESCRIPTION_MAX_LENGTH),
                    persona: input.promptText,
                    tags: [],
                    visibility: "PRIVATE",
                    parent_category: category,
                    parent_category_id: categoryId,
                },
                {
                    onSuccess: () => {
                        toast.success("Preset saved", {
                            description: `“${input.title}” added to ${presetParentCategoryPillLabel(category)}.`,
                        });
                    },
                    onError: (error: any) => {
                        toast.error("Failed to save preset", {
                            description: error.response?.data?.message,
                        });
                    },
                }
            );
        },
        [createPresetMutation, parentCategories]
    );

    return {
        presets,
        isLoadingPresets: isLoadingPresets || isLoadingFavorites,
        toggleFavorite,
        createPreset,
        hasNextPage,
        isFetchingNextPage,
        fetchNextPage,
    };
}
